export type Benefit = {
  icon: string;
  iconClass: string;
  textClass?: string;
  label: Array<string | { [key: string]: string | number }>;
};

export enum PlanId {
  Free = 'free',
  Standard = 'standard',
  Pro = 'pro',
  Enterprise = 'enterprise',
}

export enum PlanPeriod {
  Monthly = 'monthly',
  Yearly = 'yearly',
}

export type PlanConfig = {
  title: string;
  planId: PlanId;
  benefits: Benefit[];
};

export const categories = [
  'billing.category.workspaceMembers',
  'billing.category.spaces',
  'billing.category.boards',
  'billing.category.cardsAndPages',
  'billing.category.views',
  'billing.category.fileSize',
  'billing.category.privateSpaces',
  'billing.category.userPermissions',
  'billing.category.guestAccounts',
];

const planLimits = {
  free: {
    spaces: 2,
    boards: 4,
    // file size in megabytes (MB)
    fileSizeLimit: 5,
    aiNotes: 10,
    voiceTaskCreation: 10,
  },
  standard: {
    spaces: 5,
    boards: 10,
    aiNotes: 10,
  },
};

const freePlanBenefitBase = {
  icon: 'check-square',
  iconClass: 'bg-gray-500',
};

const paidPlanBenefitBase = {
  icon: 'check-square',
  iconClass: 'bg-success-500',
};

export const planData: Record<string, PlanConfig> = {
  [PlanId.Free]: {
    title: 'Free',
    planId: PlanId.Free,
    benefits: [
      { ...freePlanBenefitBase, label: ['billing.unlimitedMembers'] },
      {
        ...freePlanBenefitBase,
        label: ['billing.numberBoards', { number: planLimits.free.boards }],
      },
      {
        ...freePlanBenefitBase,
        label: ['billing.numberSpaces', { number: planLimits.free.spaces }],
      },
      {
        ...freePlanBenefitBase,
        label: ['billing.fileSizeLimit', { size: planLimits.free.fileSizeLimit }],
      },
      { ...freePlanBenefitBase, label: ['billing.sprints'] },
      { ...freePlanBenefitBase, label: ['billing.unlimitedCards'] },
      { ...freePlanBenefitBase, label: ['billing.unlimitedPages'] },
      { ...freePlanBenefitBase, label: ['billing.sharePagesOnTheWeb'] },
    ],
  },
  [PlanId.Standard]: {
    title: 'Standard',
    planId: PlanId.Standard,
    benefits: [
      {
        ...paidPlanBenefitBase,
        label: ['billing.numberBoards', { number: planLimits.standard.boards }],
      },
      {
        ...paidPlanBenefitBase,
        label: ['billing.numberSpaces', { number: planLimits.standard.spaces }],
      },
      { ...paidPlanBenefitBase, label: ['billing.unlimitedFileUploads'] },
      { ...paidPlanBenefitBase, label: ['billing.projectsAndRoadmap'] },
      { ...paidPlanBenefitBase, label: ['billing.adminRolesAndPermissions'] },
      { ...paidPlanBenefitBase, label: ['billing.cardAndPageTemplates'] },
      { ...paidPlanBenefitBase, label: ['billing.indexSharedPages'] },
      { ...paidPlanBenefitBase, label: ['billing.customViews'] },
      { ...paidPlanBenefitBase, label: ['billing.timelineLayout'] },
      { ...paidPlanBenefitBase, label: ['billing.calendarLayout'] },
    ],
  },
  [PlanId.Pro]: {
    title: 'Pro',
    planId: PlanId.Pro,
    benefits: [
      { ...paidPlanBenefitBase, label: ['billing.unlimitedBoards'] },
      { ...paidPlanBenefitBase, label: ['billing.unlimitedSpaces'] },
      { ...paidPlanBenefitBase, label: ['billing.privateSpaces'] },
      { ...paidPlanBenefitBase, label: ['billing.guestUsers'] },
      { ...paidPlanBenefitBase, label: ['billing.meetingTranscription'] },
      { ...paidPlanBenefitBase, label: ['billing.aiNoteTemplates'] },
      { ...paidPlanBenefitBase, label: ['billing.askAiSearch'] },
    ],
  },
  [PlanId.Enterprise]: {
    title: 'Enterprise',
    planId: PlanId.Enterprise,
    benefits: [
      { ...paidPlanBenefitBase, label: ['billing.ssoAndSaml'] },
      { ...paidPlanBenefitBase, label: ['billing.customBilling'] },
      { ...paidPlanBenefitBase, label: ['billing.prioritySupport'] },
    ],
  },
};

export default planData;
